import { createAmplifyContext } from 'aws-amplify';
import { createUserPoolsTokenProvider } from 'aws-amplify/adapter-core';
import { confirmResetPassword, confirmSignIn, confirmSignUp, fetchAuthSession, resendSignUpCode, resetPassword, setUpTOTP, signIn, signOut, signUp, updateMFAPreference, updatePassword, verifyTOTPSetup, } from 'aws-amplify/auth';
/**
 * Token storage for Amplify's token provider. `memory` (default, ADR-0021: sessions held in
 * memory) is a plain Map — gone on reload, invisible to other tabs. `session` is
 * `sessionStorage`: per tab, cleared when the tab closes, survives a same-tab redirect
 * round-trip (Stripe Checkout). Never `localStorage`.
 */
export function createTokenStorage(kind) {
    if (kind === 'session') {
        return {
            setItem: (key, value) => {
                sessionStorage.setItem(key, value);
                return Promise.resolve();
            },
            getItem: (key) => Promise.resolve(sessionStorage.getItem(key)),
            removeItem: (key) => {
                sessionStorage.removeItem(key);
                return Promise.resolve();
            },
            clear: () => {
                sessionStorage.clear();
                return Promise.resolve();
            },
        };
    }
    const map = new Map();
    return {
        setItem: (key, value) => {
            map.set(key, value);
            return Promise.resolve();
        },
        getItem: (key) => Promise.resolve(map.get(key) ?? null),
        removeItem: (key) => {
            map.delete(key);
            return Promise.resolve();
        },
        clear: () => {
            map.clear();
            return Promise.resolve();
        },
    };
}
/**
 * One isolated Amplify context per adapter: no `Amplify.configure()` global, no Hub events,
 * no `localStorage`. Only the user-pool token provider is installed (no identity pool, no AWS
 * credentials); every call goes to Cognito directly from the browser.
 */
export function createAmplifyAuthApi(config, storage = 'memory') {
    const authConfig = {
        Cognito: { userPoolId: config.userPoolId, userPoolClientId: config.clientId },
    };
    const tokenProvider = createUserPoolsTokenProvider(authConfig, createTokenStorage(storage));
    const ctx = createAmplifyContext({ Auth: authConfig }, { Auth: { tokenProvider } });
    return {
        signIn: (input) => signIn(ctx, input),
        confirmSignIn: (input) => confirmSignIn(ctx, input),
        fetchAuthSession: (options) => fetchAuthSession(ctx, options),
        signOut: (input) => signOut(ctx, input),
        resetPassword: (input) => resetPassword(ctx, input),
        confirmResetPassword: (input) => confirmResetPassword(ctx, input),
        updatePassword: (input) => updatePassword(ctx, input),
        setUpTOTP: () => setUpTOTP(ctx),
        verifyTOTPSetup: (input) => verifyTOTPSetup(ctx, input),
        updateMFAPreference: (input) => updateMFAPreference(ctx, input),
        signUp: (input) => signUp(ctx, input),
        confirmSignUp: (input) => confirmSignUp(ctx, input),
        resendSignUpCode: (input) => resendSignUpCode(ctx, input),
    };
}
//# sourceMappingURL=amplify-api.js.map